import React, { useState, useEffect, useLayoutEffect, useContext, useRef } from "react";
import ApiTask from "../../../app/api/admin/task/ApiTask";
import DateLib from "../../../inc/DateLib";
import Page from "../../modules/page";
import { ContextContent } from "../../modules/content/contentContext";
import Loader from "../../components/loader";
import TaskScreensModal from "./screens";
import "./styles.css";

// Страница «Задачи»: активные (status=running) + в очереди (Laravel jobs, ещё не подхвачены
// queue:work — окно обычно секунды, там часто пусто) сверху с отменой, ниже — недавние
// (последние 24ч, success/error/cancelled) по всем трём семьям скриптов разом. Poll — активные/
// очередь меняются быстро, недавние — вместе с ними же (один и тот же ответ TasksController).
const POLL_MS = 4000;

const STATUS_LABELS = { success: 'Успешно', error: 'Ошибка', cancelled: 'Отменено', running: 'Выполняется' };
const STATUS_BADGES = { success: 'badge_online', error: 'badge_offline', cancelled: 'badge_warn', running: 'badge_warn' };
const FAMILY_LABELS = { device: 'Устройство', node: 'Нода', redroid: 'Redroid' };
const KIND_LABELS = { script: 'Скрипт', 'install-apk': 'Установка APK', 'change-type': 'Смена тарифа' };

export default function TasksIndex({ ...other }) {
    return <Page {...other}>
        <TasksIndexContent/>
    </Page>;
}

function TasksIndexContent() {
    const contextContent = useContext(ContextContent);
    const [_data, set_data] = useState({ active: [], queued: [], recent: [] });
    const [_busyKey, set_busyKey] = useState(null); // family:id либо job:id, пока идёт отмена
    // Открытая лента сканов — {id, label} запуска. Сканы пишет только device-семья
    // (BaseDeviceScript::scanScreen), у node/redroid их не бывает.
    const [_screensOf, set_screensOf] = useState(null);
    // Только ПЕРВАЯ загрузка: страница опрашивает сервер раз в POLL_MS, и показывать индикатор
    // на каждом тике значит мигать им вечно. До первого ответа пустые секции неотличимы от
    // «ничего не выполняется» — вот это и нужно развести.
    const [_isFirstLoad, set_isFirstLoad] = useState(true);
    const pollTimer = useRef(null);

    useLayoutEffect(() => {
        contextContent.setTitle('Задачи');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const load = async () => {
        const data = await ApiTask.Index().getResponse();
        if (data) set_data({ active: data.active ?? [], queued: data.queued ?? [], recent: data.recent ?? [] });
        set_isFirstLoad(false);
    };

    useEffect(() => {
        load();
        pollTimer.current = setInterval(load, POLL_MS);
        return () => clearInterval(pollTimer.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const cancelRun = async (family, id) => {
        const key = `${family}:${id}`;
        if (_busyKey === key) return;
        set_busyKey(key);
        await ApiTask.Cancel(family, id).getResponse();
        set_busyKey(null);
        load();
    };

    const cancelQueued = async (jobId) => {
        const key = `job:${jobId}`;
        if (_busyKey === key) return;
        set_busyKey(key);
        await ApiTask.CancelQueued(jobId).getResponse();
        set_busyKey(null);
        load();
    };

    return <div className="tasks">
        <section className="tasks__section">
            <h3 className="tasks__section-title">Активные ({_data.active.length})</h3>
            {_isFirstLoad ? <Loader/> : _data.active.length === 0 ? (
                <div className="tasks__empty">Сейчас ничего не выполняется</div>
            ) : (
                <div className="tasks__rows">
                    {_data.active.map((row) => {
                        const key = `${row.family}:${row.id}`;
                        return <div className="tasks__row" key={key}>
                            <span className="tasks__family">{FAMILY_LABELS[row.family] ?? row.family}</span>
                            <span className="tasks__label">{row.label}</span>
                            <span className="tasks__target">{row.target}</span>
                            <span className="tasks__time">с {DateLib.formatterDateTime(row.created_at)}</span>
                            {row.family === 'device' ? (
                                <button
                                    type="button"
                                    className="tasks__screens-btn"
                                    onClick={() => set_screensOf({ id: row.id, label: row.label })}
                                >
                                    Сканы
                                </button>
                            ) : null}
                            {row.cancel_requested ? (
                                <span className="tasks__cancelling">Останавливается…</span>
                            ) : (
                                <button
                                    type="button"
                                    className="tasks__cancel-btn"
                                    disabled={_busyKey === key}
                                    onClick={() => cancelRun(row.family, row.id)}
                                >
                                    Отменить
                                </button>
                            )}
                        </div>;
                    })}
                </div>
            )}
        </section>

        <section className="tasks__section">
            <h3 className="tasks__section-title">В очереди ({_data.queued.length})</h3>
            {_isFirstLoad ? <Loader/> : _data.queued.length === 0 ? (
                <div className="tasks__empty">Очередь пуста</div>
            ) : (
                <div className="tasks__rows">
                    {_data.queued.map((row) => {
                        const key = `job:${row.job_id}`;
                        return <div className="tasks__row" key={key}>
                            <span className="tasks__family">{KIND_LABELS[row.kind] ?? row.kind}</span>
                            <span className="tasks__target">{row.target}</span>
                            <span className="tasks__time">{DateLib.formatterDateTime(row.created_at)}</span>
                            <button
                                type="button"
                                className="tasks__cancel-btn"
                                disabled={_busyKey === key}
                                onClick={() => cancelQueued(row.job_id)}
                            >
                                Отменить
                            </button>
                        </div>;
                    })}
                </div>
            )}
        </section>

        <section className="tasks__section">
            <h3 className="tasks__section-title">Недавние (24ч)</h3>
            {_isFirstLoad ? <Loader/> : _data.recent.length === 0 ? (
                <div className="tasks__empty">За последние сутки скриптов не было</div>
            ) : (
                <table className="tasks__table">
                    <thead>
                        <tr>
                            <th>Тип</th>
                            <th>Скрипт</th>
                            <th>Цель</th>
                            <th>Статус</th>
                            <th>Когда</th>
                            <th>Сканы</th>
                        </tr>
                    </thead>
                    <tbody>
                        {_data.recent.map((row) => (
                            <tr key={`${row.family}:${row.id}`}>
                                <td>{FAMILY_LABELS[row.family] ?? row.family}</td>
                                <td>{row.label}</td>
                                <td>{row.target}</td>
                                <td>
                                    <span className={`badge ${STATUS_BADGES[row.status] ?? ''}`}>
                                        {STATUS_LABELS[row.status] ?? row.status}
                                    </span>
                                </td>
                                <td>{DateLib.formatterDateTime(row.created_at)}</td>
                                <td>
                                    {row.family === 'device' ? (
                                        <button
                                            type="button"
                                            className="tasks__screens-btn"
                                            onClick={() => set_screensOf({ id: row.id, label: row.label })}
                                        >
                                            Сканы
                                        </button>
                                    ) : '—'}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </section>

        {_screensOf ? <TaskScreensModal
            executeId={_screensOf.id}
            label={_screensOf.label}
            onClose={() => set_screensOf(null)}
        /> : null}
    </div>;
}
